import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../services/axiosConfig';
import CommunicationUsageTable from './CommunicationUsageTable';
import CommunicationLimitSettings from './CommunicationLimitSettings';

const CommunicationAnalytics = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState('usage');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/communication-analytics', { params: { days } });
      setData(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to load communication analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading && !data) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  }

  const summary = data?.summary || {};
  const ratesConfigured = data?.configuration?.smsRate && data?.configuration?.emailRate;
  const stats = [
    { label: 'SMS sent', value: summary.smsMessages || 0, detail: `${summary.smsSegments || 0} billable segments` },
    { label: 'Emails sent', value: summary.emails || 0, detail: 'Campaign recipients delivered' },
    { label: 'Failed sends', value: summary.failed || 0, detail: 'SMS and email combined' },
    { label: ratesConfigured ? 'Estimated spend' : 'Tracked spend', value: summary.estimatedCost === null ? (summary.actualSmsCost > 0 ? `$${summary.actualSmsCost.toFixed(2)}` : '—') : `$${summary.estimatedCost.toFixed(2)}`, detail: ratesConfigured ? 'Using configured provider rates' : 'Provider charges appear after processing' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 4 }}>
        <Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/master-dashboard')} sx={{ mb: 1 }}>National overview</Button>
          <Typography variant="h3" component="h1" fontWeight={700}>Communication Analytics</Typography>
          <Typography color="text.secondary">Compare church SMS and email usage, delivery failures, and cost-driving volume.</Typography>
        </Box>
        {activeTab === 'usage' && <ButtonGroup aria-label="Analytics period">
          {[7, 30, 90, 'all'].map((period) => (
            <Button key={period} variant={days === period ? 'contained' : 'outlined'} onClick={() => setDays(period)}>
              {period === 'all' ? 'All time' : `${period} days`}
            </Button>
          ))}
        </ButtonGroup>}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} aria-label="Communication analytics sections" sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="usage" label="Usage" />
        <Tab value="limits" label="Limits" />
      </Tabs>

      {activeTab === 'usage' ? <>
        {!ratesConfigured && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Usage is being tracked. Cost estimates will appear once your provider rates are configured.
          </Alert>
        )}
        <Paper variant="outlined" sx={{ mb: 4, overflow: 'hidden' }}>
          <Grid container>
            {stats.map(({ label, value, detail }, index) => (
              <Grid item xs={12} sm={6} lg={3} key={label} sx={{ p: 3, borderRight: { lg: index < 3 ? '1px solid' : 0 }, borderBottom: { xs: index < 3 ? '1px solid' : 0, lg: 0 }, borderColor: 'divider' }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>{label}</Typography>
                <Typography variant="h3" fontWeight={700} color={index === 2 && value > 0 ? 'error.main' : 'text.primary'}>{typeof value === 'number' ? value.toLocaleString() : value}</Typography>
                <Typography variant="body2" color="text.secondary">{detail}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Usage by church</Typography>
        <CommunicationUsageTable organizations={data?.organizations || []} />
      </> : (
        <CommunicationLimitSettings organizations={data?.organizations || []} onSaved={loadAnalytics} />
      )}
    </Container>
  );
};

export default CommunicationAnalytics;
